import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ShoppingBag, ShoppingCart, CreditCard, PackageCheck, Zap, ShieldCheck, 
  ArrowRight, Sparkles, Rocket, Calendar
} from "lucide-react";
import { 
  SiShopify, SiWoocommerce, SiStripe, SiRazorpay, 
  SiReact, SiNodedotjs 
} from "react-icons/si";

export default function EcommerceService() {
  const capabilities = [
    {
      icon: <ShoppingCart className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Shopify Store Engineering",
      desc: "Custom Shopify Liquid theme setup, App Store plugin integrations, custom section building, and speed-optimized storefronts."
    },
    {
      icon: <ShoppingBag className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "WooCommerce & Custom E-Stores",
      desc: "High-performance WordPress WooCommerce setups with unlimited product catalogs, scalable databases, and custom checkout flows."
    },
    {
      icon: <CreditCard className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Payment Gateway Integration",
      desc: "Secure integration with Razorpay, Stripe, PayPal, UPI, EMI options, and international currency conversions."
    },
    {
      icon: <PackageCheck className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Inventory & Logistics Automation",
      desc: "Automated order tracking, inventory sync, Shiprocket/Delhivery logistics APIs, and ERP integrations."
    },
    {
      icon: <Zap className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "High-Conversion Checkout UX",
      desc: "Streamlined 1-click checkout flows, abandoned cart recovery automation, cross-selling, and upsell modules."
    },
    {
      icon: <ShieldCheck className="text-blue-600 dark:text-cyan-400" size={24} />,
      title: "Security & PCI Compliance",
      desc: "PCI-DSS compliance standards, SSL encryption, customer data privacy protection, and automated daily store backups."
    }
  ];

  const techStackData = [
    {
      name: "Shopify",
      category: "E-Commerce Platform",
      icon: React.createElement(SiShopify as any, { className: "text-[#96BF48]", size: 32 })
    },
    {
      name: "Shopify Plus",
      category: "Enterprise Storefront",
      icon: React.createElement(SiShopify as any, { className: "text-[#5E8E3E]", size: 32 })
    },
    {
      name: "WooCommerce",
      category: "WordPress E-Store",
      icon: React.createElement(SiWoocommerce as any, { className: "text-[#96588A]", size: 32 })
    },
    {
      name: "Stripe",
      category: "Global Payment Gateway",
      icon: React.createElement(SiStripe as any, { className: "text-[#635BFF]", size: 32 })
    },
    {
      name: "Razorpay",
      category: "UPI & Cards Gateway",
      icon: React.createElement(SiRazorpay as any, { className: "text-[#02042B] dark:text-[#3395FF]", size: 32 })
    },
    {
      name: "Liquid",
      category: "Shopify Template Engine",
      icon: (
        <svg viewBox="0 0 24 24" className="w-8 h-8 fill-[#96BF48]" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2.69l5.66 5.66a8 8 0 11-11.31 0z" />
        </svg>
      )
    },
    {
      name: "React",
      category: "Headless Storefront UI",
      icon: React.createElement(SiReact as any, { className: "text-[#61DAFB]", size: 32 })
    },
    {
      name: "Node.js",
      category: "Custom Store Backend",
      icon: React.createElement(SiNodedotjs as any, { className: "text-[#5FA04E]", size: 32 })
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-200 font-sans py-12 md:py-20 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-16">
        
        {/* HERO */}
        <div className="text-center max-w-4xl mx-auto space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 text-blue-700 dark:text-blue-400">
            <Sparkles size={14} />
            <span className="text-xs font-bold uppercase tracking-wider">
              E-Commerce Excellence
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-slate-900 dark:text-white tracking-tight leading-tight">
            🛒 Turnkey <span className="text-blue-600 dark:text-cyan-400">E-Commerce Solutions</span>
          </h1>

          <p className="text-slate-600 dark:text-slate-400 text-base sm:text-lg max-w-2xl mx-auto font-medium leading-relaxed">
            Engineered to turn visitors into repeat buyers. High-converting online stores built on Shopify, WooCommerce, and custom full-stack e-commerce architectures.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4 pt-2">
            <Link 
              to="/apply/website?plan=E-Commerce%20Suite" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-blue-600 hover:bg-blue-700 active:bg-blue-800 dark:bg-blue-500 dark:hover:bg-blue-600 !text-white shadow-lg shadow-blue-600/25 transition-all cursor-pointer"
            >
              <Rocket size={18} className="!text-white" />
              <span className="!text-white font-bold">Apply for E-Commerce Plan</span>
              <ArrowRight size={16} className="!text-white" />
            </Link>

            <Link 
              to="/Pricing" 
              className="h-12 px-6 rounded-xl font-semibold text-sm flex items-center gap-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-800 text-slate-800 dark:text-slate-200 hover:border-blue-500 transition-all cursor-pointer shadow-sm"
            >
              <Calendar size={18} />
              <span>View E-Commerce Pricing</span>
            </Link>
          </div>
        </div>

        {/* VISUAL E-COMMERCE TECH STACK GRID */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <span className="text-xs font-bold uppercase tracking-widest text-blue-600 dark:text-cyan-400 bg-blue-50 dark:bg-blue-500/10 px-3 py-1 rounded-full border border-blue-200 dark:border-blue-500/20">
              E-Commerce Ecosystem
            </span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              Supported E-Commerce Platforms & Payment Stacks
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
              We integrate enterprise shopping engines, custom storefronts, and seamless payment gateways.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-4">
            {techStackData.map((tech, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ scale: 1.04, y: -2 }}
                className="bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700/60 p-4 rounded-2xl flex flex-col items-center justify-center text-center space-y-2.5 transition-all shadow-sm hover:shadow-md hover:border-blue-500/40 cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 flex items-center justify-center shadow-sm">
                  {tech.icon}
                </div>
                <div>
                  <h4 className="text-xs font-extrabold text-slate-900 dark:text-white leading-tight">{tech.name}</h4>
                  <span className="text-[10px] font-semibold text-slate-500 dark:text-slate-400 block mt-0.5">{tech.category}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CAPABILITIES */}
        <div className="space-y-8">
          <div className="text-center max-w-2xl mx-auto space-y-2">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
              E-Commerce Store Features
            </h2>
            <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm">
              Everything required to launch, manage, and scale a high-volume online storefront.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {capabilities.map((cap, idx) => (
              <motion.div 
                key={idx}
                whileHover={{ y: -4 }}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-6 space-y-4 shadow-md flex flex-col justify-between"
              >
                <div className="space-y-3">
                  <div className="w-12 h-12 rounded-xl bg-blue-50 dark:bg-blue-500/15 border border-blue-200 dark:border-blue-500/30 flex items-center justify-center">
                    {cap.icon}
                  </div>
                  <h3 className="text-lg font-extrabold text-slate-900 dark:text-white">
                    {cap.title}
                  </h3>
                  <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed">
                    {cap.desc}
                  </p>
                </div>

                <div className="pt-2 border-t border-slate-100 dark:border-slate-800/80">
                  <Link to="/enquirynow" className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-cyan-400 hover:underline">
                    <span>Enquire For Store</span>
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA BANNER */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 dark:from-blue-900/90 dark:to-indigo-950/90 text-white rounded-3xl p-8 sm:p-12 text-center space-y-6 shadow-2xl">
          <h2 className="text-3xl sm:text-4xl font-extrabold tracking-tight">
            Launch Your Online Store Today
          </h2>
          <p className="text-blue-100 max-w-xl mx-auto text-sm font-medium">
            Contact our e-commerce engineering specialists for a custom store demo and proposal.
          </p>
          <div className="flex flex-wrap justify-center gap-4 pt-2">
            <Link 
              to="/apply/website?plan=E-Commerce%20Suite" 
              className="h-12 px-8 rounded-xl font-bold text-sm bg-white text-blue-700 hover:bg-slate-100 shadow-md flex items-center gap-2 cursor-pointer"
            >
              <span>Apply for E-Commerce Suite</span>
              <ArrowRight size={16} />
            </Link>
          </div>
        </div>

      </div>
    </div>
  );
}
